import React, { useState, useMemo, useCallback, useRef } from "react";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import {
  MAP_LIBRARIES,
  START_FLAG,
  END_FLAG,
  buildRoute,
  isTower,
  latestPosition,
  MAP_FALLBACK_CENTER,
  RouteLines,
  RouteFlags,
  RouteLegend,
} from "components/RoutePlot";

const mapContainerStyle = { height: "500px", width: "100%" };

const RouteMapView = ({ mapInfo, overlayImg }) => {
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(14);
  const mapRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: MAP_LIBRARIES,
    version: "3.64",
  });

  const routeModel = useMemo(() => buildRoute(mapInfo), [mapInfo]);

  const latest = useMemo(() => latestPosition(mapInfo), [mapInfo]);

  const poleIcon = useMemo(() => {
    if (!isLoaded || !window.google) return null;
    const size = { scaledSize: new window.google.maps.Size(28, 36), anchor: new window.google.maps.Point(8, 34) };
    return { start: { url: START_FLAG, ...size }, end: { url: END_FLAG, ...size } };
  }, [isLoaded]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onZoomChanged = useCallback(() => {
    if (mapRef.current) setCurrentZoom(mapRef.current.getZoom());
  }, []);

  if (!isLoaded) return <div>Loading map...</div>;

  const ends = routeModel.segments.length ? routeModel.segments : null;
  const startPoint = ends ? ends[0].path[0] : null;
  const endSegment = ends ? ends[ends.length - 1] : null;
  const endPoint = endSegment ? endSegment.path[endSegment.path.length - 1] : null;
  const focus = latest ? latest.position : MAP_FALLBACK_CENTER;

  return (
    <div className="relative">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={focus}
        zoom={currentZoom}
        onLoad={onMapLoad}
        onZoomChanged={onZoomChanged}
      >
        <RouteLines segments={routeModel.segments} />
        <RouteFlags flags={routeModel.flags} onSelect={setSelectedMarker} />

        {startPoint && (
          <Marker position={startPoint} zIndex={4} title="Start" icon={poleIcon?.start} />
        )}
        {endPoint && (
          <Marker position={endPoint} zIndex={4} title="End" icon={poleIcon?.end} />
        )}
        {latest && (
          <Marker
            position={latest.position}
            zIndex={5}
            icon={{
              url: overlayImg,
              scaledSize: new window.google.maps.Size(30, 50),
            }}
            onClick={() => setSelectedMarker({ position: latest.position, point: latest.point, kind: null, batteryLow: false, level: null })}
          />
        )}

        {selectedMarker && (
          <InfoWindow
            position={selectedMarker.position}
            onCloseClick={() => setSelectedMarker(null)}
          >
            <div>
              <b>{selectedMarker.kind?.label || (selectedMarker.batteryLow ? "Battery low" : "Live point")}</b><br />
              <b>Time:</b> {selectedMarker.point.timestamp || selectedMarker.point.serverts}<br />
              <b>Direction:</b> {selectedMarker.point.dirn ?? selectedMarker.point.direction}<br />
              <b>Speed:</b> {selectedMarker.point.speed ?? selectedMarker.point.gspeed} km/h<br />
              <b>Position:</b> {isTower(selectedMarker.point) ? "TOWER" : "GPS"}<br />
              {selectedMarker.level != null && <><b>Battery:</b> {selectedMarker.level}%</>}
              {selectedMarker.kind && selectedMarker.batteryLow && <><br /><b>Battery low</b></>}
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
      <RouteLegend />
    </div>
  );
};

export default RouteMapView;
